/**
 * The transaction Loss/Gain "difference" — one rule for every document flow:
 *
 *     Difference = Source Quantity − Destination Quantity
 *     > 0 ⇒ Loss     < 0 ⇒ Gain     == 0 ⇒ balanced
 *
 * Loss/gain TYPES are classifications only; they never modify the formula. A line is either a
 * loss or a gain, never both. The sign convention applies to bags (NoB) as well, never abs()'d.
 * Tare counts as a loss: the received quantity is the GROSS weighed amount.
 *
 * Per-line only. The header-level pro-rata weighbridge split that vnr-prod layered on top was
 * removed by decision A3 and is deliberately not here.
 */
import { Decimal, toDecimal, ZERO, type DecimalInput } from "./decimal";

export type EndpointKind = "vendor" | "customer" | "storage" | "activity";

export const LOSS_GAIN_TOLERANCE = new Decimal("0.01");

/** What the document's form config says about how this flow measures itself. */
export interface LossGainFlow {
  sourceKind: EndpointKind;
  destKind: EndpointKind;
  /** Lots arrive with an upstream declaration (PDN / S-Capture / Lotgen feed). */
  declaredFeed: boolean;
  /** The form captures a Received quantity column (false ⇒ declared IS received; bag diff = 0). */
  receivedCaptured: boolean;
}

export interface LossGainLine {
  /** Gross transaction quantity leaving the source. */
  quantity: DecimalInput;
  /** What was confirmed at the destination (gross weighed; tare not subtracted). */
  receivedQuantity: DecimalInput;
  /** Declared quantity (PDN qty / DC qty), when the arrival declares up front. */
  declaredQty?: DecimalInput;
  /** Bin allocations at the destination. */
  storages?: ReadonlyArray<{ quantity: DecimalInput; bags?: DecimalInput }>;
  bags?: DecimalInput;
  receivedBags?: DecimalInput;
  declaredBags?: DecimalInput;
}

export interface DifferenceResult {
  difference: Decimal;
  differenceBags: Decimal;
  source: Decimal;
  destination: Decimal;
}

export function computeDifference(
  flow: LossGainFlow,
  line: LossGainLine
): DifferenceResult {
  const isStorageDest = flow.destKind === "storage";
  const isVendorIn = flow.sourceKind === "vendor";
  const isVendorToActivity = isVendorIn && flow.destKind === "activity";
  const isVendorToStorage = isVendorIn && isStorageDest;
  const isFeed = flow.declaredFeed;
  const isFeedArrival = isFeed && (isVendorToActivity || isVendorToStorage);

  const qty = toDecimal(line.quantity);
  const recv = toDecimal(line.receivedQuantity);
  const storages = line.storages ?? [];
  const storageQty = storages.reduce(
    (acc, s) => acc.plus(toDecimal(s.quantity)),
    ZERO
  );
  const declared = toDecimal(line.declaredQty);

  // A DECLARED vendor arrival measures itself against its declaration whoever declared it
  // (Core feed or the operator typing off the vendor's challan): PRESENCE is the test.
  const isDeclaredVendorArrival =
    (isVendorToActivity || isVendorToStorage) && declared.gt(0);

  let source: Decimal;
  if (isFeedArrival) source = declared.gt(0) ? declared : recv;
  else if (isDeclaredVendorArrival) source = declared;
  else if (isVendorToActivity && isFeed) source = declared;
  else if (isVendorIn && isFeed) source = recv;
  else source = qty; // no declaration: gross weighed qty, exactly as before

  // "What landed" is the bin total ONLY when material actually goes to bins.
  const destination = isStorageDest ? storageQty : recv;

  // Bags: identical model.
  const storageBags = storages.reduce(
    (acc, s) => acc.plus(toDecimal(s.bags)),
    ZERO
  );
  const qtyBags = toDecimal(line.bags);
  let recvBags = toDecimal(line.receivedBags);
  const declaredBags = toDecimal(line.declaredBags);
  // No Received column ⇒ no received bag count either; the declared count IS the received count.
  if (!flow.receivedCaptured) recvBags = qtyBags;

  let sourceBags: Decimal;
  if (isFeedArrival) sourceBags = declared.gt(0) ? declaredBags : recvBags;
  else if (isDeclaredVendorArrival) sourceBags = declaredBags;
  else if (isVendorToActivity && isFeed) sourceBags = declaredBags;
  else if (isVendorIn && isFeed) sourceBags = recvBags;
  else sourceBags = qtyBags;
  const destinationBags = isStorageDest ? storageBags : recvBags;

  return {
    difference: source.minus(destination),
    differenceBags: sourceBags.minus(destinationBags),
    source,
    destination,
  };
}

export function isBalanced(
  difference: DecimalInput,
  tolerance: Decimal = LOSS_GAIN_TOLERANCE
): boolean {
  return toDecimal(difference).abs().lte(tolerance);
}

// ---------------------------------------------------------------- loss/gain entries

export interface LossTypeDef {
  id: string;
  /** True = a loss reason, false = a gain reason. */
  isLoss: boolean;
  /** Arrival Loss / Arrival Gain: derived from the Difference, never typed. */
  isAutoCalculated: boolean;
}

export interface LossEntry {
  lossTypeId: string;
  value: DecimalInput;
}

const fmt = (d: Decimal) => d.toFixed();

/**
 * Fill the auto-calculated loss/gain entries from the Difference and return the new entry list.
 *
 *     Arrival Loss = difference  if > 0 else 0
 *     Arrival Gain = −difference if < 0 else 0
 *
 * The matching-side auto type keeps a non-blank client value ONLY on a form that also carries a
 * manual reason (so an operator can split the variance across reasons; reconciliation still
 * enforces totals). Everywhere else it is the full Difference — a client value on an auto-only
 * form can only be stale, and honouring it once booked −3,842 kg in production. The wrong side is
 * always forced to 0. Manual entries are left untouched.
 */
export function applyAutoLossGain(
  lossTypes: readonly LossTypeDef[],
  entries: readonly LossEntry[],
  difference: DecimalInput
): LossEntry[] {
  const autoTypes = lossTypes.filter((t) => t.isAutoCalculated);
  if (!autoTypes.length) return [...entries];
  const hasManualTypes = lossTypes.some((t) => !t.isAutoCalculated);

  const d = toDecimal(difference);
  const lossValue = d.gt(0) ? d : ZERO;
  const gainValue = d.lt(0) ? d.neg() : ZERO;

  const out = entries.map((e) => ({ ...e }));
  for (const lt of autoTypes) {
    const isMatchingSide = (lt.isLoss && d.gt(0)) || (!lt.isLoss && d.lt(0));
    const defaultValue = lt.isLoss ? lossValue : gainValue;
    const existing = out.find((e) => e.lossTypeId === lt.id);
    const clientBlank =
      existing === undefined ||
      existing.value === null ||
      existing.value === undefined ||
      existing.value === "";
    const value =
      isMatchingSide && !clientBlank && hasManualTypes
        ? fmt(toDecimal(existing.value))
        : fmt(defaultValue);
    if (existing) existing.value = value;
    else out.push({ lossTypeId: lt.id, value });
  }
  return out;
}

/** Zero every entry (a balanced line has nothing to classify; stale values are dropped, not rejected). */
export function zeroLossGainEntries(
  entries: readonly LossEntry[]
): LossEntry[] {
  return entries.map((e) => ({ ...e, value: "0" }));
}

export function lossGainTotals(
  lossTypes: readonly LossTypeDef[],
  entries: readonly LossEntry[]
): { totalLoss: Decimal; totalGain: Decimal } {
  const byId = new Map(lossTypes.map((t) => [t.id, t]));
  let totalLoss = ZERO;
  let totalGain = ZERO;
  for (const e of entries) {
    const t = byId.get(e.lossTypeId);
    if (!t) continue; // unknown type: ignored here, rejected by schema validation upstream
    if (t.isLoss) totalLoss = totalLoss.plus(toDecimal(e.value));
    else totalGain = totalGain.plus(toDecimal(e.value));
  }
  return { totalLoss, totalGain };
}

/**
 * Entered loss/gain totals must reconcile to the Difference. Returns the violation message, or
 * null when the line reconciles.
 *  - a line cannot carry both loss and gain;
 *  - difference > tol ⇒ total loss == difference, no gain entries;
 *  - difference < −tol ⇒ total gain == |difference|, no loss entries;
 *  - balanced ⇒ no entries at all.
 */
export function validateLossReconciliation(
  difference: DecimalInput,
  lossTypes: readonly LossTypeDef[],
  entries: readonly LossEntry[],
  tolerance: Decimal = LOSS_GAIN_TOLERANCE
): string | null {
  const { totalLoss, totalGain } = lossGainTotals(lossTypes, entries);
  const d = toDecimal(difference);
  if (totalLoss.gt(0) && totalGain.gt(0))
    return "A line cannot record both Loss and Gain at the same time.";
  if (d.gt(tolerance)) {
    if (totalGain.gt(0)) return "Difference is a Loss; remove Gain entries.";
    if (totalLoss.minus(d).abs().gt(tolerance))
      return `Entered Loss total (${fmt(totalLoss)}) must equal the Difference (${fmt(d)}).`;
    return null;
  }
  if (d.lt(tolerance.neg())) {
    if (totalLoss.gt(0)) return "Difference is a Gain; remove Loss entries.";
    if (totalGain.minus(d.neg()).abs().gt(tolerance))
      return `Entered Gain total (${fmt(totalGain)}) must equal abs(Difference) (${fmt(d.neg())}).`;
    return null;
  }
  if (totalLoss.gt(0) || totalGain.gt(0))
    return "No variance for this line; remove the Loss/Gain entries.";
  return null;
}

/** True when the client's difference drifts from the server's beyond tolerance. */
export function differenceDrifts(
  server: DecimalInput,
  client: DecimalInput,
  tolerance: Decimal = LOSS_GAIN_TOLERANCE
): boolean {
  if (client === null || client === undefined || client === "") return false;
  return toDecimal(server).minus(toDecimal(client)).abs().gt(tolerance);
}
